import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Drawer, makeStyles } from '@material-ui/core';
import ActionButton from '../actionButton';
import VirtualizedList from '../virtualizedList';
import VirtualComment from './virtualComment';
import { isObjectEmpty, getPersonShortName } from '../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  drawerRoot: {
    zIndex: `1501 !important`,
  },
  middleWidth: {
    height: '100vh',
    overflow: 'hidden',
    width: 940,
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: 71,
    padding: spacing(6, 6, 6, 8),
    borderBottom: `1px solid ${primary.bluish5}`,
  },
  closeButton: {
    display: 'flex',
    marginLeft: 'auto',
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: spacing(6, 8),
    minHeight: 0,
    width: '100%',
  },
  scrollY: {
    right: -19,
  },
}));

const ReviewCommentsDrawer = ({
  isOpened,
  comments,
  commentator = {},
  translations,
  onClose,
  isViewAll = false,
}) => {
  const classes = useStyles();

  const title = useMemo(() => {
    if (isViewAll) {
      return translations.allComments;
    }

    if (!isObjectEmpty(commentator)) {
      return `${getPersonShortName(
        commentator
      )} ${translations.comments.toLowerCase()}`;
    }

    return translations.questionComments;
  }, [translations, commentator, isViewAll]);

  const getVirtualCommentSize = useCallback(() => 220, []);

  const renderComment = useCallback(
    comment => {
      return <VirtualComment translations={translations} comment={comment} />;
    },
    [translations]
  );

  return (
    <Drawer
      anchor="right"
      open={isOpened}
      classes={{
        root: classes.drawerRoot,
        paper: classes.middleWidth,
      }}
      onClose={onClose}
    >
      <div className={classNames(classes.header)}>
        <Typography variant="h4">{title}</Typography>
        <ActionButton
          className={classes.closeButton}
          type={ACTION_BUTTON_TYPES.CLOSE}
          onClickHandler={onClose}
        />
      </div>
      {isOpened && (
        <div className={classes.content}>
          <VirtualizedList
            verticalScrollClass={classes.scrollY}
            items={comments}
            count={comments?.length ?? 0}
            gap={32}
            estimateSize={getVirtualCommentSize}
            renderItem={renderComment}
            isDynamicSize
          />
        </div>
      )}
    </Drawer>
  );
};

ReviewCommentsDrawer.propTypes = {
  translations: PropTypes.object.isRequired,
  isViewAll: PropTypes.bool,
  isOpened: PropTypes.bool.isRequired,
  comments: PropTypes.arrayOf(PropTypes.object).isRequired,
  commentator: PropTypes.shape({}),
  onClose: PropTypes.func.isRequired,
};

export default ReviewCommentsDrawer;
